// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Sort } from '@/constants/sort';
import {
  ProjectFilter,
  ProjectSortProperty,
  getProjects,
} from '@/project/api/get-projects';
import {
  ONE_MINUTE_MS,
  TEN_MINUTES_MS,
  TWO_MINUTES_MS,
} from '@/utilities/time-constants';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

const DEFAULT_SORT: Sort<ProjectSortProperty> = {
  property: 'name',
  direction: 'asc',
};

/**
 * Creates a stable string representation of a project filter for use in query keys.
 * Every filter field must be represented here, otherwise toggling it leaves the
 * query key unchanged and the list serves stale cached data.
 */
export function serializeProjectFilter(filter?: ProjectFilter): string {
  if (!filter) return '';

  // Create a normalized version without mutating the original
  return JSON.stringify({
    query: filter.query || undefined,
    statuses: filter.statuses ? [...filter.statuses].sort() : undefined,
    syncStatuses: filter.syncStatuses
      ? [...filter.syncStatuses].sort()
      : undefined,
    isArchived: filter.isArchived,
    productFulfillmentStatus: filter.productFulfillmentStatus
      ? [...filter.productFulfillmentStatus].sort()
      : undefined,
    projectFulfillmentStatus: filter.projectFulfillmentStatus
      ? [...filter.projectFulfillmentStatus].sort()
      : undefined,
    productIds: filter.productIds ? [...filter.productIds].sort() : undefined,
    projectIds: filter.projectIds ? [...filter.projectIds].sort() : undefined,
    companyTypes: filter.companyTypes
      ? [...filter.companyTypes].sort()
      : undefined,
    customerIds: filter.customerIds
      ? [...filter.customerIds].sort()
      : undefined,
    locationIds: filter.locationIds
      ? [...filter.locationIds].sort()
      : undefined,
    workflowStageIds: filter.workflowStageIds
      ? [...filter.workflowStageIds].sort()
      : undefined,
    startBy: filter.startBy,
    startClassifications: filter.startClassifications
      ? [...filter.startClassifications].sort()
      : undefined,
    endBy: filter.endBy,
    endClassifications: filter.endClassifications
      ? [...filter.endClassifications].sort()
      : undefined,
    pageSize: filter.pageSize,
    pageNumber: filter.pageNumber,
    sort: filter.sort ? JSON.stringify(filter.sort) : undefined,
  });
}

interface UseProjectsOptions {
  sort?: Sort<ProjectSortProperty>;
  filter?: ProjectFilter;
}

/**
 * A React hook that requests a collection of projects from the remote API.
 *
 * @param options.sort - The sort order of the projects.
 * @param options.filter - The filter to apply to the projects.
 */
export function useProjects(options: UseProjectsOptions = {}) {
  const sort = options.sort ?? DEFAULT_SORT;

  // Merge sort into filter for backend processing
  const filterWithSort = useMemo(
    () => ({
      ...options.filter,
      sort,
    }),
    [options.filter, sort]
  );

  // Create stable query key by serializing the combined filter
  const filterString = serializeProjectFilter(filterWithSort);

  // Memoize the query key to ensure referential equality
  const queryKey = useMemo(
    () => ['projects', { filterString }],
    [filterString]
  );

  // Create a stable query function that doesn't change unless the actual filter changes
  const queryFn = useCallback(async () => {
    const { projects, totalCount } = await getProjects(filterWithSort);
    return { projects, totalCount };
  }, [filterWithSort]);

  const { data, isLoading, isFetching, error, refetch, isPending } = useQuery({
    queryKey,
    queryFn,
    staleTime: ONE_MINUTE_MS,
    gcTime: TEN_MINUTES_MS,
    refetchInterval: TWO_MINUTES_MS,
    refetchOnWindowFocus: true,
  });

  // `data` and `totalCount` are `null` while the current query key is loading.
  // Consumers must treat `null` as "not yet known" and render a skeleton;
  // surfacing `[]` / `0` here fabricates an empty result set and can show an
  // empty state, "0 items" export label, or "1-0 of 0" footer before the real
  // data arrives.
  return {
    data: data?.projects ?? null,
    totalCount: data?.totalCount ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
    isPending,
  };
}
